import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getToken } from '../services/authApi';

export interface SavedPropertyList {
  id: string;
  name: string;
  propertyIds: number[];
  createdAt: string;
}

const STORAGE_KEY = 'property-saved-lists';

export const createSavedPropertyList = (name: string, propertyIds: number[] = []): SavedPropertyList => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: name.trim(),
  propertyIds: Array.from(new Set(propertyIds.filter((id) => Number.isFinite(id)))),
  createdAt: new Date().toISOString(),
});

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normalizeLists(raw: unknown): SavedPropertyList[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const id = String(row.id || '').trim();
      const name = String(row.name || '').trim();
      if (!id || !name) return null;
      const propertyIds = Array.isArray(row.propertyIds)
        ? Array.from(new Set(row.propertyIds.map((value) => Number(value)).filter((value) => Number.isFinite(value))))
        : [];
      return {
        id,
        name,
        propertyIds,
        createdAt: String(row.createdAt || ''),
      } satisfies SavedPropertyList;
    })
    .filter((item): item is SavedPropertyList => item !== null);
}

export const loadSavedPropertyLists = (): SavedPropertyList[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return normalizeLists(JSON.parse(raw));
  } catch {
    return [];
  }
};

export const saveSavedPropertyLists = (lists: SavedPropertyList[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lists));
};

export async function fetchSavedPropertyLists(): Promise<SavedPropertyList[]> {
  const local = loadSavedPropertyLists();
  if (!getToken()) {
    return local;
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/property-lists`, { headers: authHeaders() });
    const remote = normalizeLists(response.data?.lists);
    if (remote.length > 0) {
      saveSavedPropertyLists(remote);
      return remote;
    }
    if (local.length > 0) {
      await persistSavedPropertyLists(local);
      return local;
    }
    return [];
  } catch {
    return local;
  }
}

export async function persistSavedPropertyLists(lists: SavedPropertyList[]): Promise<void> {
  saveSavedPropertyLists(lists);
  if (!getToken()) return;
  try {
    await axios.put(
      `${API_BASE_URL}/property-lists`,
      { lists },
      { headers: authHeaders() }
    );
  } catch {
    // Keep local cache if the network is unavailable.
  }
}
